import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  ICertificateTemplateRepository,
  CreateCertificateTemplateData,
  UpdateCertificateTemplateData,
  GetCertificateTemplateData,
  CertificateVerificationResult,
} from './interfaces/certificate-template.repository.interface';

export class CertificateTemplateRepository implements ICertificateTemplateRepository {
  private collectionName = 'certificateTemplates';
  private certificatesCollection = 'certificates';

  async getCertificateTemplates(): Promise<GetCertificateTemplateData[]> {
    try {
      const colRef = collection(db, this.collectionName);
      const snapshot = await getDocs(colRef);
      return snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as unknown as GetCertificateTemplateData[];
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CERT_TEMPLATE_GET_ALL_FAILED',
        devMessage: 'Failed to get certificate templates',
        cause: error,
        level: 'error',
      });
    }
  }

  async getDefaultCertificateTemplate(): Promise<GetCertificateTemplateData | null> {
    try {
      const colRef = collection(db, this.collectionName);
      const q = query(colRef, where('isDefault', '==', true));
      const snapshot = await getDocs(q);

      if (snapshot.docs.length === 0) return null;

      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      } as unknown as GetCertificateTemplateData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CERT_TEMPLATE_GET_DEFAULT_FAILED',
        devMessage: 'Failed to get default certificate template',
        cause: error,
        level: 'error',
      });
    }
  }

  async createCertificateTemplate(
    templateData: CreateCertificateTemplateData,
  ): Promise<GetCertificateTemplateData> {
    try {
      const colRef = collection(db, this.collectionName);
      // Owner recorded so "My Templates" lists it and the rules let its
      // instructor edit it; nothing set this, so every template was orphaned.
      const docRef = await addDoc(colRef, {
        ...templateData,
        createdById: auth.currentUser?.uid ?? null,
        isDefault: templateData.isDefault ?? false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...templateData,
        isDefault: templateData.isDefault ?? false,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as GetCertificateTemplateData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CERT_TEMPLATE_CREATE_FAILED',
        devMessage: `Failed to create certificate template "${templateData.name}"`,
        cause: error,
        level: 'error',
      });
    }
  }

  async updateCertificateTemplate(
    templateId: string,
    updateData: UpdateCertificateTemplateData,
  ): Promise<GetCertificateTemplateData> {
    try {
      const docRef = doc(db, this.collectionName, templateId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const updated = await getDoc(docRef);
      return {
        id: templateId,
        ...updated.data(),
      } as unknown as GetCertificateTemplateData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CERT_TEMPLATE_UPDATE_FAILED',
        devMessage: `Failed to update certificate template ${templateId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteCertificateTemplate(templateId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, templateId);
      await deleteDoc(docRef);
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CERT_TEMPLATE_DELETE_FAILED',
        devMessage: `Failed to delete certificate template ${templateId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async setDefaultCertificateTemplate(
    templateId: string,
  ): Promise<GetCertificateTemplateData> {
    try {
      const colRef = collection(db, this.collectionName);

      // Remove default from all existing defaults
      const q = query(colRef, where('isDefault', '==', true));
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach((d) => {
        batch.update(d.ref, { isDefault: false });
      });

      // Set the new default
      const newDefaultRef = doc(db, this.collectionName, templateId);
      batch.update(newDefaultRef, { isDefault: true, updatedAt: serverTimestamp() });

      await batch.commit();

      const updated = await getDoc(newDefaultRef);
      return {
        id: templateId,
        ...updated.data(),
      } as unknown as GetCertificateTemplateData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'CERT_TEMPLATE_SET_DEFAULT_FAILED',
        devMessage: `Failed to set default certificate template ${templateId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async verifyCertificate(verificationCode: string): Promise<CertificateVerificationResult> {
    try {
      const certsRef = collection(db, this.certificatesCollection);
      const q = query(certsRef, where('verificationCode', '==', verificationCode));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return {
          valid: false,
          certificate: null,
          message: 'Certificate not found or invalid verification code',
        };
      }

      const certificate = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      return {
        valid: true,
        certificate,
        message: 'Certificate is valid and verified',
      };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      return {
        valid: false,
        certificate: null,
        message: 'Certificate verification failed',
      };
    }
  }
}
